import React, { useState } from "react";
import { ref, set, push, get } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { X, Truck } from "lucide-react";

const RecordShopDeliveryModal = ({ shop, companyName, onClose }) => {
  const [quantity, setQuantity] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [driverName, setDriverName] = useState("");
  const [loading, setLoading] = useState(false);

  if (!shop) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quantity || parseFloat(quantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    setLoading(true);
    try {
      const deliveryId = push(ref(database, "temp")).key;
      const deliveryDate = new Date().toISOString();
      const year = new Date().getFullYear();
      const month = new Date().toLocaleString("en-US", { month: "long" });

      const deliveryData = {
        deliveryId,
        shopId: shop.id,
        shopName: shop.shopName,
        quantity: parseFloat(quantity),
        driverName: driverName || "N/A",
        deliveryNotes,
        deliveryDate,
        recordedBy: companyName,
        status: "Completed",
      };

      // Save delivery record
      await set(
        ref(database, `Companies/${companyName}/ShopDeliveries/${shop.id}/${year}/${month}/${deliveryId}`),
        deliveryData
      );

      // Update shop stock
      const currentStock = parseFloat(shop.currentStock) || 0;
      const newStock = currentStock + parseFloat(quantity);
      
      const shopRef = ref(database, `Companies/${companyName}/Shops/${shop.id}`);
      const shopSnapshot = await get(shopRef);
      const currentShopData = shopSnapshot.val() || shop;
      
      await set(shopRef, {
        ...currentShopData,
        currentStock: newStock,
        totalDeliveries: (currentShopData.totalDeliveries || 0) + 1,
        lastDeliveryDate: deliveryDate,
      });

      toast.success(`Delivery of ${quantity} liters recorded successfully`);
      onClose();
    } catch (error) {
      console.error("Error recording delivery:", error);
      toast.error("Failed to record delivery. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-cyan-700 flex items-center gap-2">
            <Truck size={24} />
            Record Shop Delivery
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={24} />
          </button>
        </div>

        <div className="mb-4 p-3 bg-cyan-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Shop:</strong> {shop.shopName}
          </p>
          <p className="text-sm text-gray-700">
            <strong>Current Stock:</strong> {parseFloat(shop.currentStock || 0).toLocaleString()} liters
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity Delivered (Liters) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Enter quantity in liters"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Driver Name (Optional)
              </label>
              <input
                type="text"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Enter driver name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery Notes (Optional)
              </label>
              <textarea
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                rows="3"
                placeholder="Any notes about this delivery..."
              />
            </div>

            {quantity && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>New Stock Level:</strong>{" "}
                  {(parseFloat(shop.currentStock || 0) + parseFloat(quantity)).toLocaleString()} liters
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:bg-cyan-300"
              disabled={loading}
            >
              {loading ? "Recording..." : "Record Delivery"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordShopDeliveryModal;
